import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Dossier } from '@/mocks/dossiers';

export default function DossierToc({ dossier }: { dossier: Dossier }) {
  const items = useMemo(
    () => [
      { id: 'question', label: '核心疑问' },
      ...dossier.sections.map((section, index) => ({ id: `section-${index}`, label: section.heading })),
      { id: 'timeline', label: '时间线关键节点' },
      { id: 'foreshadows', label: '被忽略的伏笔' },
      { id: 'connections', label: '事件隐藏关联' },
      { id: 'takeaways', label: '结论要点' },
    ],
    [dossier],
  );

  const [active, setActive] = useState('question');

  useEffect(() => {
    const onScroll = () => {
      let current = items[0]?.id ?? '';
      items.forEach((item) => {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 150) {
          current = item.id;
        }
      });
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [items]);

  return (
    <aside className="space-y-6 lg:sticky lg:top-24">
      <nav aria-label="文章目录" className="rounded-lg border border-background-200 bg-background-100 p-5">
        <p className="font-label text-xs tracking-[0.24em] text-foreground-500">本文目录</p>
        <ul className="mt-4 space-y-1">
          {items.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={`block border-l-2 py-1.5 pl-3 font-label text-sm transition-colors ${
                  active === item.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="rounded-lg border border-background-200 bg-background-100 p-5">
        <p className="font-label text-xs tracking-[0.24em] text-foreground-500">档案信息</p>
        <dl className="mt-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <dt className="font-label text-xs text-foreground-500">整理</dt>
            <dd className="font-body text-sm text-foreground-800">{dossier.author}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="font-label text-xs text-foreground-500">发布</dt>
            <dd className="font-body text-sm text-foreground-800">{dossier.date}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="font-label text-xs text-foreground-500">阅读时长</dt>
            <dd className="font-body text-sm text-foreground-800">{dossier.readTime}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="font-label text-xs text-foreground-500">节点数量</dt>
            <dd className="font-body text-sm text-foreground-800">{dossier.timeline.length} 个</dd>
          </div>
        </dl>
      </div>

      <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
        <p className="font-heading text-base font-semibold text-foreground-950">手上有补充线索？</p>
        <p className="mt-2 font-body text-xs leading-relaxed text-foreground-700">
          请附上原始素材出现的时间与来源，我们会做交叉核对后更新本档案的时间线。
        </p>
        <Link
          to="/contact"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-send-plane-line" />
          提交线索
        </Link>
      </div>
    </aside>
  );
}